import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, CalendarPlus, CheckCircle2, Lock, MapPin, Star } from 'lucide-react';
import {
  Alert, Button, ConfirmDialog, DetailHeader, DetailSection, EmptyState, ErrorState, Facts, Field, InfoTip, Input, SearchSelect,
  StatusBadge, TextSkeleton, Textarea, cn, useToast,
} from '@dru/ui';
import { PARTNER_TYPE_LABEL } from '@dru/types';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { formatDate, formatDateTime } from '../../../lib/format';
import { CaseJourney } from '../../../components/CaseJourney';
import { assignmentChange, initialBranch } from '../../../lib/assignment';
import { nextStep } from '../components/caseNextStep';
import { EXCEPTION_LABEL, EXCEPTION_TONE, PHYSICIAN_WAIT_LABEL, humanStatus, statusLabel, typeLabel, type OpsCase } from './Cases';

interface SuggestedLab {
  partnerId: string;
  partnerName: string;
  partnerType: string;
  matchType: 'FULL' | 'PARTIAL';
  mandatoryTestsTotal: number;
  mandatoryTestsCovered: number;
  missingMandatoryTests: Array<{ code: string; name: string }>;
  isPreferred: boolean;
  preferredNote?: string | null;
  proximity: { distanceKm: number | null; matchLevel: string; originUsed?: string };
  capacity?: { dailyCapacity: number; availableCapacity: number; targetDate: string };
  branches: Array<{ id: string; name: string; city: string | null; isPrimary: boolean }>;
}

interface PhysicianOption { id: string; fullName: string; registrationNo: string }

/**
 * The reason fields' floor, as the API enforces it (cases.controller Reason).
 *
 * A reason is asked for only to override — moving a routed case to another
 * lab or branch. Routing a new case and booking at its lab need none (flow
 * audit 2026-10-20, 5.7); a note is still accepted and recorded.
 */
const MIN_REASON = 10;

/** How many ranked labs are open before "show more": the decision is almost always among the first few. */
const SHOWN = 4;

/** Where a case can be moved to another lab (examination.service REASSIGNABLE). */
const REASSIGNABLE = new Set(['ROUTED', 'CONSENT_PENDING', 'SCHEDULED', 'NO_SHOW_OPEN']);
const ACTIVE_APPOINTMENT = new Set(['SCHEDULED', 'CONFIRMED']);

/** Why the lab cannot be changed any more, in the words of where the case is. */
function lockedBecause(status: string): string {
  switch (status) {
    case 'DRAFT': return 'The company has not raised this case yet, so there is nothing to route.';
    case 'CHECKED_IN': case 'IN_PROGRESS':
      return 'The employee has been checked in at this lab, so the lab can no longer be changed.';
    case 'LAB_PENDING': case 'REVIEW_PENDING': case 'RETEST_REQUIRED':
      return 'The examination has taken place at this lab, so the assignment is part of the record and cannot be changed.';
    default: return 'This case is closed; its assignment is part of the record.';
  }
}

/** `datetime-local` wants the reader's own clock, to the minute. */
function localNow() {
  const d = new Date(Date.now() + 5 * 60_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * One case, and Operations' decision on it (docs/37 A5, D7).
 *
 * The page renders the operational projection — there is nothing clinical in
 * it to show — and the ranked labs that can run the case's tests. Moving a
 * routed case to another lab needs a reason, which is written to the audit
 * trail with the act, and a partial capability match needs an override
 * reason; routing a new case or booking at its lab takes an optional note.
 *
 * What is offered follows where the case is, as the API decides it:
 *  - REQUESTED: route it. A lab already on the request is shown as proposed,
 *    not as assigned — routing is still Operations' act.
 *  - Routed but not yet seen (REASSIGNABLE): book the appointment if none is
 *    booked, or change the lab — which cancels the booking, so it is folded
 *    away and confirmed.
 *  - After check-in, or closed: nothing to change, and the page says why
 *    instead of offering a button the API would refuse.
 *
 * The ranking is the server's (examination.service eligiblePartners): full
 * capability first, then HR's preferred labs, then distance. Each lab is a
 * card with the two facts the choice turns on — which required tests it
 * cannot run, and how far it is — and the decision is summarised beside the
 * list before it is committed.
 */
/**
 * Keyed by the record, so moving from one case to another starts clean: a
 * half-filled form or an open panel belongs to the record it was opened on.
 */
export function CaseDetail() {
  const { id = '' } = useParams();
  return <CaseDetailPage key={id} />;
}

function CaseDetailPage() {
  const { tenantId, id } = useParams<{ tenantId: string; id: string }>();
  const { toast } = useToast();
  const { permissions } = useAuth();
  const allowed = holds(permissions, 'case.read-ops');
  const canRoute = holds(permissions, 'examination.route');
  const kase = useQuery<OpsCase>(
    () => (allowed ? api.get(`/platform/tenants/${tenantId}/cases/${id}`) : Promise.reject(new Error('The case queue is for Operations.'))),
    [tenantId, id, allowed],
  );
  const status = kase.data?.status;
  const routable = status === 'REQUESTED';
  const reassignable = !!status && REASSIGNABLE.has(status);
  // The ranking is only asked for when a lab can actually be chosen.
  const labs = useQuery<{ items: SuggestedLab[] }>(
    () => (canRoute && (routable || reassignable)
      ? api.get(`/platform/tenants/${tenantId}/cases/${id}/suggested-labs`)
      : Promise.resolve({ items: [] })),
    [tenantId, id, canRoute, routable, reassignable],
  );
  // The list's default is "can sign": active, AFIH and linked to a console
  // login. An entry that cannot sign is not offered — a case routed to one
  // reaches review and can never be certified — and the API refuses the
  // assignment as well (`physician-cannot-sign`).
  const atReview = status === 'REVIEW_PENDING';
  const physicians = useQuery<{ items: PhysicianOption[] }>(
    () => (canRoute && (routable || reassignable || atReview) ? api.get('/physicians?limit=100') : Promise.resolve({ items: [] })),
    [canRoute, routable, reassignable, atReview],
  );

  const [partnerId, setPartnerId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [physicianId, setPhysicianId] = useState('');
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const [slot, setSlot] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Moving a routed case is the exception, and it cancels bookings. The
  // comparison stays folded until somebody asks for it, so a case that is on
  // track does not open on an invitation to reroute it. Booking at the lab
  // it already has is its own, smaller form.
  const [mode, setMode] = useState<'closed' | 'change' | 'book'>('closed');

  const c = kase.data;
  const ranked = labs.data?.items ?? [];
  const chosen = ranked.find((l) => l.partnerId === partnerId);
  const partial = chosen?.matchType === 'PARTIAL';
  const moving = reassignable && !!c?.assignedPartner;
  const hasActiveAppointment = !!c?.appointment && ACTIVE_APPOINTMENT.has(c.appointment.status);
  // What the submission would actually change. Choosing the current lab again
  // keeps its current branch, so looking at the assignment can never quietly
  // move the case to the main site; and an unchanged assignment is not
  // submittable at all. A lab proposed on a REQUESTED case is not "current":
  // routing it is a first assignment.
  const current = {
    partnerId: moving ? c?.assignedPartner?.id ?? null : null,
    branchId: moving ? c?.assignedLocation?.id ?? null : null,
    physicianId: c?.physician?.id ?? null,
    hasActiveAppointment,
  };
  const { labChanged, nothingChanges, cancelsAppointments, canBook, actionLabel } = assignmentChange(current, {
    partnerId, branchId, physicianId, slot,
  });
  const slotPast = !!slot && new Date(slot).getTime() < Date.now();
  // Only moving a routed case to another lab or branch is an override.
  const needsReason = moving && labChanged;
  const reasonShort = reason.trim().length > 0 && reason.trim().length < MIN_REASON;
  const missing = [
    !partnerId && 'Choose a lab',
    nothingChanges && 'Choose a different lab, branch or physician, or a new appointment',
    partial && override.trim().length < MIN_REASON && 'Explain why the missing tests are acceptable',
    slotPast && 'Choose an appointment time in the future',
    needsReason && reason.trim().length < MIN_REASON && `Say why the lab is changing (at least ${MIN_REASON} characters)`,
    !needsReason && reasonShort && `Make the note at least ${MIN_REASON} characters, or leave it empty`,
  ].filter(Boolean) as string[];
  const ready = missing.length === 0;
  const branch = chosen?.branches.find((b) => b.id === branchId) ?? null;
  const physician = physicians.data?.items.find((p) => p.id === physicianId) ?? null;

  function choose(lab: SuggestedLab) {
    setPartnerId(lab.partnerId);
    // A branch belongs to one lab; carrying it across would book the wrong
    // site. The current lab starts on its current branch, not the main site.
    setBranchId(initialBranch(lab.partnerId, current));
    setOverride('');
  }

  function reset() {
    setReason(''); setOverride(''); setSlot(''); setPartnerId(''); setBranchId(''); setPhysicianId('');
    setConfirming(false); setError(null); setMode('closed');
  }

  function startBooking() {
    reset();
    setPartnerId(current.partnerId ?? '');
    setBranchId(current.branchId ?? '');
    setMode('book');
  }

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/platform/tenants/${tenantId}/cases/${id}/assign`, {
        partnerId,
        ...(branchId ? { partnerLocationId: branchId } : {}),
        ...(physicianId ? { physicianId } : {}),
        ...(partial ? { overrideReason: override } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(slot && canBook ? { appointment: { scheduledFor: new Date(slot).toISOString(), slotMinutes: 30 } } : {}),
      }, crypto.randomUUID());
      toast('success', mode === 'book' ? 'Appointment booked' : moving ? 'Lab changed' : 'Lab assigned');
      reset();
      kase.reload();
      labs.reload();
    } catch (e) {
      setError(errorMessage(e));
      setConfirming(false);
    } finally { setBusy(false); }
  }

  if (!allowed) {
    return (
      <div className="space-y-4">
      <PageHeader className="mb-0" title="Medical case" />
      <EmptyState
        icon={<Lock className="h-5 w-5" />}
        title="Medical cases are for Operations"
        hint={holds(permissions, 'case.read-support')
          ? 'Search the employee’s name or mobile in the box at the top: it shows where and when, without anything clinical.'
          : 'Your role does not route medical cases.'}
      />
      </div>
    );
  }
  if (kase.loading) return <TextSkeleton lines={6} />;
  if (kase.error || !c) {
    return <ErrorState title="Could not open this case" detail={kase.error ?? 'Case not found'} onRetry={kase.reload} />;
  }

  const routedLab = !routable && c.assignedPartner
    ? [c.assignedPartner.name, c.assignedLocation?.name].filter(Boolean).join(' · ') : null;
  const visible = showAll ? ranked : ranked.slice(0, SHOWN);
  const choosing = canRoute && (routable || (reassignable && mode === 'change'));

  return (
    <div className="space-y-5">
      <DetailHeader
        title={c.employee.fullName}
        subtitle={
          <span>
            <span className="font-mono">{c.employee.employeeCode}</span>
            {' · '}{c.company.name}{c.plant ? ` · ${c.plant.name}` : ''}
            {' · '}{typeLabel(c)}
          </span>
        }
        badges={
          <>
            {/* One value, as on the list: the problem, or else the stage. */}
            {c.exceptions.length === 0 && <StatusBadge tone="neutral">{statusLabel(c.status)}</StatusBadge>}
            {c.exceptions.map((e) => (
              <StatusBadge key={e} tone={EXCEPTION_TONE[e] ?? 'neutral'}>{EXCEPTION_LABEL[e] ?? e}</StatusBadge>
            ))}
          </>
        }
      />


      <NextStepBanner
        kase={c}
        canRoute={canRoute}
        reassignable={reassignable}
        hasActiveAppointment={hasActiveAppointment}
        onBook={startBooking}
      />

      {atReview && (
        <PhysicianReviewSection
          kase={c}
          tenantId={tenantId!}
          canRoute={canRoute}
          options={physicians.data?.items ?? []}
          onDone={() => kase.reload()}
        />
      )}

      <CaseJourney
        status={c.status}
        lab={routedLab}
        appointmentAt={c.appointment && !['CANCELLED', 'RESCHEDULED', 'NO_SHOW'].includes(c.appointment.status) ? c.appointment.scheduledFor : null}
        formatWhen={formatDateTime}
      />

      <DetailSection title="Case">
        <Facts
          columns={4}
          facts={[
            {
              label: 'Lab',
              value: routedLab ? c.assignedPartner!.name
                : <span className="text-neutral-500">Not routed yet</span>,
              hint: routedLab
                ? (c.assignedLocation ? [c.assignedLocation.name, c.assignedLocation.city].filter(Boolean).join(', ') : undefined)
                : c.assignedPartner?.name ? `Proposed: ${c.assignedPartner.name}` : undefined,
            },
            { label: 'Physician', value: c.physician?.fullName ?? <span className="text-neutral-500">Not chosen</span> },
            {
              label: 'Appointment',
              value: c.appointment ? formatDateTime(c.appointment.scheduledFor) : <span className="text-neutral-500">Not booked</span>,
              hint: c.appointment ? APPOINTMENT_STATUS[c.appointment.status] ?? humanStatus(c.appointment.status) : undefined,
            },
            {
              label: c.slaDueAt ? 'SLA due' : 'Due',
              value: c.slaDueAt ?? c.dueDate ? <DueValue iso={(c.slaDueAt ?? c.dueDate)!} breached={!!c.slaBreachedAt} /> : <span className="text-neutral-500">No date</span>,
              hint: c.slaDueAt ?? c.dueDate ? formatDate((c.slaDueAt ?? c.dueDate)!) : undefined,
            },
            // HR's preferred lab, only when HR gave one.
            ...(c.requestedLocation || c.requestedLocationNote ? [{
              label: 'HR’s preference',
              wide: true,
              value: c.requestedLocation
                ? `${c.requestedLocation.partnerName ?? ''} · ${c.requestedLocation.name}${c.requestedLocation.city ? `, ${c.requestedLocation.city}` : ''}`
                : <span className="text-neutral-500">No lab named</span>,
              hint: c.requestedLocationNote ? `“${c.requestedLocationNote}”` : undefined,
            }] : []),
          ]}
        />
      </DetailSection>


      {/* Nothing to choose: the assignment as it stands, and why it stands. */}
      {!choosing && mode !== 'book' && (
        <DetailSection
          title="Lab assignment"
          action={canRoute && reassignable && (
            <div className="flex flex-wrap gap-2">
              {!hasActiveAppointment && (
                <Button size="sm" onClick={startBooking}>
                  <CalendarPlus className="h-3.5 w-3.5" aria-hidden /> {c.status === 'NO_SHOW_OPEN' ? 'Rebook appointment' : 'Book appointment'}
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => { reset(); setMode('change'); }}>Change lab…</Button>
            </div>
          )}
        >
          <p className="text-small text-neutral-700">
            {routedLab
              ? <>Assigned to <strong className="font-semibold text-neutral-900">{routedLab}</strong>.</>
              : 'No lab assigned.'}
            {' '}
            {reassignable
              ? (canRoute
                ? (hasActiveAppointment
                  ? 'Changing the lab cancels the booked appointment and needs a reason for the audit trail.'
                  : 'Nothing is booked yet. Book the appointment at this lab, or change the lab first.')
                : 'Your role cannot change the lab.')
              : lockedBecause(c.status)}
          </p>
        </DetailSection>
      )}

      {/* Booking at the lab the case already has. */}
      {mode === 'book' && canRoute && (
        <section aria-labelledby="book-heading" className="rounded-card border border-neutral-200 bg-white p-4 shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="book-heading" className="text-h4 text-neutral-900">
              {c.status === 'NO_SHOW_OPEN' ? 'Rebook the appointment' : 'Book the appointment'}
            </h2>
            <Button variant="ghost" size="sm" onClick={reset}>Cancel</Button>
          </div>
          <p className="mt-0.5 text-small text-neutral-500">
            At <strong className="font-medium text-neutral-800">{routedLab}</strong>. The employee is sent the time and place once it is booked.
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="Date and time" required error={slotPast ? 'Choose a time in the future' : null}>
              {(p) => <Input {...p} type="datetime-local" min={localNow()} value={slot} onChange={(e) => setSlot(e.target.value)} />}
            </Field>
            <Field label="Note (optional)" hint={reasonShort ? `At least ${MIN_REASON} characters, or leave it empty.` : undefined} info="Recorded in the audit trail against your name.">
              {(p) => <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300} />}
            </Field>
          </div>
          {error && <Alert tone="danger" className="mt-3">{error}</Alert>}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={submit} loading={busy} disabled={!slot || slotPast || reasonShort}>
              Book appointment
            </Button>
            {!slot && (
              <span className="text-caption font-normal text-neutral-500">Choose a date and time</span>
            )}
          </div>
        </section>
      )}

      {choosing && (
        <section aria-labelledby="assign-heading" className="space-y-3">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1">
                <h2 id="assign-heading" className="scroll-mt-20 text-h4 text-neutral-900">
                  {moving ? 'Change the lab' : 'Route to a lab'}
                </h2>
                <InfoTip label="About the lab ranking">
                  Ranked by the server: labs that run every required test first, then HR’s preferred labs, then distance.
                </InfoTip>
              </div>
              {moving && (
                <Button variant="ghost" size="sm" onClick={reset}>
                  Keep the current lab
                </Button>
              )}
            </div>
          </div>

          <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
            <div className="space-y-3">
              {labs.error && <Alert tone="danger">{labs.error}</Alert>}
              {labs.loading && !labs.data && <TextSkeleton lines={4} />}
              {labs.data && ranked.length === 0 && (
                <Alert tone="warning" title="No lab can take this case">
                  No lab in the network can run this company’s tests. Add the tests to a lab’s capabilities, or add a lab to the network.
                </Alert>
              )}
              <div aria-label="Recommended labs" role="group" className="space-y-3">
                {visible.map((lab, i) => (
                  <LabCard
                    key={lab.partnerId}
                    lab={lab}
                    best={i === 0 && lab.matchType === 'FULL'}
                    tag={moving && c.assignedPartner?.id === lab.partnerId ? 'Current lab'
                      : !moving && c.assignedPartner?.id === lab.partnerId ? 'Proposed on the request' : null}
                    requestedBranchId={c.requestedLocation?.id ?? null}
                    selected={lab.partnerId === partnerId}
                    branchId={lab.partnerId === partnerId ? branchId : ''}
                    onSelect={() => choose(lab)}
                    onBranch={setBranchId}
                  />
                ))}
              </div>
              {ranked.length > SHOWN && (
                <Button variant="ghost" size="sm" onClick={() => setShowAll((s) => !s)}>
                  {showAll ? 'Show the first few only' : `Show ${ranked.length - SHOWN} more lab${ranked.length - SHOWN === 1 ? '' : 's'}`}
                </Button>
              )}
            </div>

            {/* The decision, summarised before it is committed. Sticky beside a
                long list, so the reader never scrolls away from what they are
                about to do. */}
            <aside
              aria-label={moving ? 'Change of lab' : 'Routing'}
              className="rounded-card border border-neutral-200 bg-white shadow-card xl:sticky xl:top-20"
            >
              <div className="border-b border-neutral-200 px-4 py-3">
                <h3 className="text-body font-semibold text-neutral-900">{moving ? 'Change of lab' : 'Routing'}</h3>
                {moving && (
                  <p className="mt-0.5 text-caption font-normal text-neutral-500">
                    Currently {routedLab}
                  </p>
                )}
              </div>
              <div className="space-y-4 px-4 py-4">
                <dl className="space-y-2 text-small">
                  <SummaryRow label="Lab" value={chosen?.partnerName ?? null} empty="Choose from the list" />
                  <SummaryRow
                    label="Branch"
                    value={chosen ? (branch ? `${branch.name}${branch.city ? `, ${branch.city}` : ''}` : 'Main site') : null}
                    empty="—"
                  />
                  <SummaryRow
                    label="Coverage"
                    value={chosen ? `${chosen.mandatoryTestsCovered} of ${chosen.mandatoryTestsTotal} required tests` : null}
                    empty="—"
                    tone={chosen ? (partial ? 'warning' : 'success') : undefined}
                  />
                </dl>

                {partial && (
                  <Field
                    label="Override reason" required
                    status={`This lab cannot run ${chosen!.missingMandatoryTests.length} required test${chosen!.missingMandatoryTests.length === 1 ? '' : 's'}. Say why it is still the right place.`}
                  >
                    {(p) => <Textarea {...p} value={override} onChange={(e) => setOverride(e.target.value)} rows={2} maxLength={300} />}
                  </Field>
                )}

                <Field
                  label="Physician"
                  status={!physicians.loading && (physicians.data?.items.length ?? 0) === 0
                    ? 'No physician on the panel can sign right now. Link a console login or record AFIH on the Physicians screen.'
                    : undefined}
                  info="Only physicians who can sign are listed: active, AFIH certified and with a console login."
                >
                  {() => (
                    <SearchSelect aria-label="Physician" value={physicianId} onChange={setPhysicianId} className="w-full"
                      placeholder={c.physician ? `Keep ${c.physician.fullName}` : 'Choose later'}
                      searchPlaceholder="Search by name or registration number…"
                      options={[
                        { value: '', label: c.physician ? `Keep ${c.physician.fullName}` : 'Choose later' },
                        ...(physicians.data?.items ?? []).map((ph) => ({ value: ph.id, label: ph.fullName, hint: ph.registrationNo ?? undefined })),
                      ]} />
                  )}
                </Field>

                {canBook && (
                  <Field
                    label={moving ? 'New appointment (optional)' : 'First appointment (optional)'}
                    error={slotPast ? 'Choose a time in the future' : null}
                  >
                    {(p) => <Input {...p} type="datetime-local" min={localNow()} value={slot} onChange={(e) => setSlot(e.target.value)} />}
                  </Field>
                )}

                <Field
                  label={needsReason ? 'Reason for the change' : 'Note (optional)'}
                  required={needsReason}
                  hint={needsReason || reasonShort ? `At least ${MIN_REASON} characters.` : undefined}
                  info={needsReason
                    ? 'Moving the case cancels the booking at the current lab, so the reason is recorded in the audit trail against your name.'
                    : 'Recorded in the audit trail against your name.'}
                >
                  {(p) => <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300} />}
                </Field>

                {cancelsAppointments && (
                  <Alert tone="warning">
                    {hasActiveAppointment
                      ? `The booked appointment on ${formatDateTime(c.appointment!.scheduledFor)} is cancelled when the lab or branch changes.`
                      : 'Any open appointment at the current lab is cancelled when the lab or branch changes.'}
                  </Alert>
                )}
                {error && <Alert tone="danger">{error}</Alert>}

                <div className="space-y-2">
                  <Button
                    className="w-full"
                    onClick={() => (moving && labChanged ? setConfirming(true) : submit())}
                    loading={busy && !(moving && labChanged)}
                    disabled={!ready}
                  >
                    {actionLabel}
                  </Button>
                  {!ready && (
                    <ul className="space-y-0.5 text-caption font-normal text-neutral-500" aria-label="Still needed">
                      {missing.map((m) => <li key={m}>· {m}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </section>
      )}

      {confirming && chosen && (
        <ConfirmDialog
          title="Change the lab?"
          what={
            <>
              This case moves from <strong>{routedLab}</strong> to <strong>{chosen.partnerName}</strong>
              {branch ? ` · ${branch.name}` : ' · main site'}.
            </>
          }
          affects={
            <>
              {c.employee.fullName} · {c.employee.employeeCode} · {c.company.name}
              {physician ? <><br />Physician: {physician.fullName}</> : null}
              {slot ? <><br />New appointment: {formatDateTime(new Date(slot).toISOString())}</> : null}
              {partial ? <><br />Cannot run {chosen.missingMandatoryTests.length} required test{chosen.missingMandatoryTests.length === 1 ? '' : 's'} — your override reason is recorded.</> : null}
            </>
          }
          irreversible={hasActiveAppointment
            ? `The booked appointment on ${formatDateTime(c.appointment!.scheduledFor)} is cancelled. The employee will need the new time and place.`
            : 'Any open appointment at the current lab is cancelled.'}
          confirmLabel={hasActiveAppointment ? 'Change lab and cancel booking' : 'Change lab'}
          tone={hasActiveAppointment ? 'danger' : 'primary'}
          onConfirm={submit}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

/**
 * The physician a case at review is waiting on, and giving it to one who can
 * take it (flow audit 2026-10-20, 12.3).
 *
 * A case used to sit here for good when its physician could not open the
 * company's records, or nobody had been assigned. The server decides who can
 * take it — active, AFIH, a login that has been accepted and can read this
 * company — and "Assign automatically" picks the available one with the
 * fewest reviews waiting.
 */
function PhysicianReviewSection({ kase: c, tenantId, canRoute, options, onDone }: {
  kase: OpsCase;
  tenantId: string;
  canRoute: boolean;
  options: PhysicianOption[];
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState<'auto' | 'named' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const waiting = c.physicianWait ?? null;

  async function assign(physicianId?: string) {
    setBusy(physicianId ? 'named' : 'auto'); setError(null);
    try {
      const next = await api.post<OpsCase>(`/platform/tenants/${tenantId}/cases/${c.id}/physician`,
        physicianId ? { physicianId } : {}, crypto.randomUUID());
      toast('success', `Assigned to ${next.physician?.fullName ?? 'a physician'}`);
      setChoice('');
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  return (
    <div id="physician-review" className="scroll-mt-20">
    <DetailSection title="Physician review">
      <p className="text-small text-neutral-700">
        {c.physician
          ? <><strong className="font-semibold text-neutral-900">{c.physician.fullName}</strong> has to review and sign this case.</>
          : 'Nobody is assigned to review this case.'}
        {' '}
        {waiting && c.physician
          ? <span className="font-medium text-overdue-600">{PHYSICIAN_WAIT_LABEL[waiting]}.</span>
          : null}
      </p>
      {canRoute && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <Button size="sm" variant={waiting ? 'primary' : 'secondary'} loading={busy === 'auto'} disabled={!!busy} onClick={() => assign()}>
            Assign automatically
          </Button>
          <span className="text-caption font-normal text-neutral-500 max-md:w-full">or choose</span>
          <SearchSelect value={choice} onChange={setChoice} aria-label="Physician" className="w-full sm:w-64" clearable
            placeholder="A physician who can sign…" searchPlaceholder="Search by name or registration number…"
            options={options.filter((p) => p.id !== c.physician?.id).map((p) => ({ value: p.id, label: p.fullName, hint: p.registrationNo ?? undefined }))} />
          <Button size="sm" variant="secondary" loading={busy === 'named'} disabled={!choice || !!busy} onClick={() => assign(choice)}>
            Assign
          </Button>
          <InfoTip label="About assigning a physician">
            Only a physician who can sign and can open this company’s records is accepted. A draft the previous
            physician began moves with the case.
          </InfoTip>
        </div>
      )}
      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}
    </DetailSection>
    </div>
  );
}

/**
 * The one thing to do on this case, and whose it is — first on the page, as
 * on a lead. When it is Operations' act and this login can take it, the
 * button goes straight there: booking opens its form; routing and physician
 * review scroll to their sections, which are already open below.
 */
function NextStepBanner({ kase: c, canRoute, reassignable, hasActiveAppointment, onBook }: {
  kase: OpsCase;
  canRoute: boolean;
  reassignable: boolean;
  hasActiveAppointment: boolean;
  onBook: () => void;
}) {
  const n = nextStep(c);
  // Only Operations' own move: someone else's is already named by the case
  // journey below ("Next: … by physician"), and saying it twice is clutter.
  if (n.team !== 'Operations') return null;
  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const action = !canRoute ? null
    : n.opsAct === 'book' && reassignable && !hasActiveAppointment
      ? <Button size="sm" onClick={onBook}><CalendarPlus className="h-3.5 w-3.5" aria-hidden /> {n.action}</Button>
      : n.opsAct === 'route'
        ? <Button size="sm" onClick={() => jump('assign-heading')}>{n.action}</Button>
        // Physician review: its buttons are the very next section.
        : null;
  return (
    <section
      aria-label="Next step"
      className={cn('flex flex-wrap items-center justify-between gap-3 rounded-card border p-4',
        'border-primary-200 bg-primary-50')}
    >
      <div className="min-w-0">
        <p className="text-caption font-medium uppercase tracking-wide text-primary-700">
          Next step · Operations
        </p>
        <p className="mt-0.5 text-body font-medium text-neutral-900">{n.action}</p>
        {!canRoute && (
          <p className="mt-0.5 text-small text-neutral-600">Your role cannot route cases; a colleague with routing rights has to take this.</p>
        )}
      </div>
      {action}
    </section>
  );
}

const APPOINTMENT_STATUS: Record<string, string> = {
  SCHEDULED: 'Booked', CONFIRMED: 'Confirmed', CANCELLED: 'Cancelled', RESCHEDULED: 'Rescheduled',
  NO_SHOW: 'Missed', CHECKED_IN: 'Attended', COMPLETED: 'Attended',
};

function SummaryRow({ label, value, empty, tone }: {
  label: string;
  value: string | null;
  empty: string;
  tone?: 'success' | 'warning';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-neutral-500">{label}</dt>
      <dd className={cn('min-w-0 text-right',
        value == null ? 'text-neutral-500'
          : tone === 'warning' ? 'font-medium text-warning-600'
            : tone === 'success' ? 'font-medium text-success-600'
              : 'font-medium text-neutral-900')}
      >
        {value ?? empty}
      </dd>
    </div>
  );
}

/** Where the lab is relative to the worker, in the words the ranking used. */
function proximityText(p: SuggestedLab['proximity']): string {
  const from = p.originUsed === 'EMPLOYEE' ? 'the employee' : p.originUsed === 'PLANT' ? 'the plant' : null;
  if (p.distanceKm != null) {
    const km = p.distanceKm < 10 ? p.distanceKm.toFixed(1) : String(Math.round(p.distanceKm));
    return `${km} km from ${from ?? 'the employee'}`;
  }
  switch (p.matchLevel) {
    case 'PINCODE_MATCH': return `Same pincode as ${from ?? 'the employee'}`;
    case 'CITY_MATCH': return `Same city as ${from ?? 'the employee'}`;
    case 'STATE_MATCH': return `Same state as ${from ?? 'the employee'}`;
    default: return 'Distance unknown';
  }
}

function LabCard({ lab, best, tag, requestedBranchId, selected, branchId, onSelect, onBranch }: {
  lab: SuggestedLab;
  best: boolean;
  /** "Current lab" on a routed case, "Proposed on the request" on one still waiting. */
  tag: string | null;
  requestedBranchId: string | null;
  selected: boolean;
  branchId: string;
  onSelect: () => void;
  onBranch: (id: string) => void;
}) {
  const full = lab.matchType === 'FULL';
  const missing = lab.missingMandatoryTests;
  const hrBranch = lab.branches.find((b) => b.id === requestedBranchId);
  return (
    <div
      className={cn(
        'rounded-card border bg-white p-4 shadow-card transition-colors duration-hover',
        selected ? 'border-primary-600 ring-1 ring-primary-600' : 'border-neutral-200',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-body-lg font-semibold text-neutral-900">{lab.partnerName}</h3>
            {best && <StatusBadge tone="info">Best match</StatusBadge>}
            {tag && <StatusBadge tone="neutral">{tag}</StatusBadge>}
          </div>
          <p className="text-caption font-normal text-neutral-500">{PARTNER_TYPE_LABEL[lab.partnerType] ?? lab.partnerType}</p>
        </div>
        <Button
          aria-pressed={selected}
          aria-label={`${selected ? 'Selected' : 'Select'} ${lab.partnerName}`}
          size="sm"
          variant={selected ? 'primary' : 'secondary'}
          onClick={onSelect}
          icon={selected ? <CheckCircle2 className="h-4 w-4" /> : undefined}
        >
          {selected ? 'Selected' : 'Select'}
        </Button>
      </div>

      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-small">
        <li className={cn('inline-flex items-center gap-1.5 font-medium', full ? 'text-success-600' : 'text-warning-600')}>
          {full ? <CheckCircle2 aria-hidden className="h-4 w-4" /> : <AlertTriangle aria-hidden className="h-4 w-4" />}
          {lab.mandatoryTestsCovered} / {lab.mandatoryTestsTotal} required tests
        </li>
        <li className="inline-flex items-center gap-1.5 text-neutral-700">
          <MapPin aria-hidden className="h-4 w-4 text-neutral-400" />
          {proximityText(lab.proximity)}
        </li>
        {lab.isPreferred && (
          <li className="inline-flex items-center gap-1.5 text-neutral-700" title={lab.preferredNote ?? undefined}>
            <Star aria-hidden className="h-4 w-4 text-neutral-400" />
            HR preferred
          </li>
        )}
        {lab.capacity && lab.capacity.dailyCapacity > 0 && (
          <li className={cn('text-neutral-600', lab.capacity.availableCapacity === 0 && 'font-medium text-warning-600')}>
            {lab.capacity.availableCapacity} of {lab.capacity.dailyCapacity} slots free on {formatDate(lab.capacity.targetDate)}
          </li>
        )}
      </ul>

      {!full && missing.length > 0 && (
        <div className="mt-3 rounded-input border border-warning-600/25 bg-warning-100 px-3 py-2 text-small">
          <p className="font-medium text-warning-600">Cannot run — an override reason is required</p>
          <p className="mt-0.5 text-neutral-800">
            {missing.slice(0, 6).map((t) => t.name).join(', ')}
            {missing.length > 6 ? `, and ${missing.length - 6} more` : ''}
          </p>
        </div>
      )}

      {hrBranch && !selected && (
        <p className="mt-2 text-caption font-normal text-neutral-500">HR asked for the {hrBranch.name} branch.</p>
      )}

      {selected && lab.branches.length > 0 && (
        <SearchSelect label="Branch" value={branchId} onChange={onBranch} className="mt-3 max-w-sm"
          placeholder="Main site" searchPlaceholder="Search branches by name or city…"
          options={[
            { value: '', label: 'Main site' },
            ...lab.branches.map((b) => ({
              value: b.id,
              label: `${b.name}${b.id === requestedBranchId ? ' — HR’s request' : ''}`,
              hint: b.city ?? undefined,
            })),
          ]} />
      )}
    </div>
  );
}

/** "In 54 days", "Due today", "3 days late". */
function DueValue({ iso, breached }: { iso: string; breached: boolean }) {
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const n = Math.round((d.getTime() - start.getTime()) / 86_400_000);
  const text = n === 0 ? 'Due today' : n < 0 ? `${-n} day${n === -1 ? '' : 's'} late` : `In ${n} day${n === 1 ? '' : 's'}`;
  return <span className={cn(n < 0 || breached ? 'text-danger-600' : n <= 7 ? 'text-warning-600' : undefined)}>{text}</span>;
}
